/**
 * Receipt review (F-RPT-N43): the draft the AI read, turned into an editable
 * form, and the form turned back into the two approval payloads. Pure, so
 * the money maths is tested without React.
 */
import type {
  ApproveReceiptExpensesInput,
  ApproveReceiptStockInput,
  ReceiptDraft,
  ReceiptScan,
  ReceiptScanOptions,
  ReceiptScanStatus,
  ReceiptStockPaidBy,
} from './receiptScanTypes'

/** A field the reader was unsure of is shown in amber. */
export const UNSURE_BELOW = 0.75

export type LineKind = 'stock' | 'expense'

export interface ReviewLine {
  key: string
  description: string
  kind: LineKind
  /** Expense lines. */
  category: string
  amount: string
  /** Stock lines: qty in the chosen unit, and the price per that unit. */
  stockItemId: string
  qty: string
  unitPrice: string
  /** 'base' = the item's stock unit; 'purchase' = its purchase unit ("Bag"). */
  unitMode: 'base' | 'purchase'
  unsure: boolean
  /** The AI's text as printed, for reference. */
  rawUnit: string
  /** The line as printed — sent on approval so the next receipt learns from it (F-RPT-N47). */
  rawText: string
  /** Matched from an earlier approved receipt (F-RPT-N47). */
  learned: boolean
  /** Stock lines (kitchen flow pilot, Batch 5): the lot's use-by date
   *  (YYYY-MM-DD; required for an item tracked for expiry) and lot number. */
  expiryDate?: string
  lotNo?: string
}

export interface ReviewForm {
  supplierId: string
  payee: string
  date: string
  paymentMethod: '' | 'cash' | 'bank' | 'card' | 'other'
  referenceNo: string
  notes: string
  total: string
  paidBy: ReceiptStockPaidBy
  buyerName: string
  lines: ReviewLine[]
}

export function num(s: string): number {
  const n = Number(String(s).replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : NaN
}

function fmtNum(n: number): string {
  if (!Number.isFinite(n) || n === 0) return ''
  return String(Math.round(n * 1000) / 1000)
}

const round2 = (n: number) => Math.round(n * 100) / 100

/** The form the review sheet opens with. */
export function formFromDraft(draft: ReceiptDraft | undefined, options: ReceiptScanOptions | undefined, today: string): ReviewForm {
  const e = draft?.extraction
  const items = new Map((options?.stock_items ?? []).map(i => [i.id, i]))
  const categories = new Set((options?.categories ?? []).map(c => c.slug))
  const lines: ReviewLine[] = (e?.lines ?? []).map((l, i) => {
    const kind: LineKind = l.kind === 'stock' ? 'stock' : 'expense'
    const item = l.stock_item_id ? items.get(l.stock_item_id) : undefined
    // A line printed in the item's purchase unit ("2 bags") opens in it.
    const unitMode: 'base' | 'purchase' =
      item?.purchase_unit_label && item.purchase_unit_factor && unitsMatch(l.unit, item.purchase_unit_label) ? 'purchase' : 'base'
    const qty = l.qty > 0 ? l.qty : l.amount > 0 && l.unit_price > 0 ? l.amount / l.unit_price : 0
    const unitPrice = l.unit_price > 0 ? l.unit_price : qty > 0 && l.amount > 0 ? l.amount / qty : 0
    return {
      key: `l${i}`,
      description: l.raw_text,
      kind,
      category: categories.has(l.category_slug) ? l.category_slug : '',
      amount: fmtNum(l.amount),
      stockItemId: item ? item.id : '',
      qty: fmtNum(qty),
      unitPrice: fmtNum(unitPrice),
      unitMode,
      // A match learned from an earlier approval is trusted, not amber.
      unsure: !l.from_history && l.confidence < UNSURE_BELOW,
      rawUnit: l.unit,
      rawText: l.raw_text,
      learned: !!l.from_history,
    }
  })
  const pm = e?.payment_hint
  return {
    supplierId: draft?.supplier_id ?? '',
    payee: draft?.supplier_id ? '' : e?.vendor_name ?? '',
    date: draft?.checks.date_issue === '' && e?.receipt_date ? e.receipt_date : today,
    paymentMethod: pm === 'cash' || pm === 'card' || pm === 'bank' ? pm : '',
    referenceNo: e?.reference_no ?? '',
    notes: '',
    total: fmtNum(e?.total ?? 0),
    paidBy: 'petty_cash',
    buyerName: '',
    lines,
  }
}

function unitsMatch(printed: string, label: string): boolean {
  const a = printed.trim().toLowerCase().replace(/s$/, '')
  const b = label.trim().toLowerCase().replace(/s$/, '')
  return a !== '' && (a === b || b.startsWith(a) || a.startsWith(b))
}

export function emptyLine(key: string, kind: LineKind = 'expense'): ReviewLine {
  return { key, description: '', kind, category: '', amount: '', stockItemId: '', qty: '', unitPrice: '', unitMode: 'base', unsure: false, rawUnit: '', rawText: '', learned: false }
}

/** The money a line stands for. */
export function lineTotal(l: ReviewLine): number {
  if (l.kind === 'expense') return num(l.amount) || 0
  const q = num(l.qty)
  const p = num(l.unitPrice)
  return q > 0 && p >= 0 ? round2(q * p) : 0
}

export interface ReviewTotals {
  expense: number
  stock: number
  lines: number
  receiptTotal: number
  /** Receipt total minus the lines; tax or a discount shows up here. */
  difference: number
}

export function reviewTotals(f: ReviewForm): ReviewTotals {
  let expense = 0
  let stock = 0
  for (const l of f.lines) {
    if (l.kind === 'expense') expense += lineTotal(l)
    else stock += lineTotal(l)
  }
  expense = round2(expense)
  stock = round2(stock)
  const receiptTotal = num(f.total) || 0
  return { expense, stock, lines: round2(expense + stock), receiptTotal, difference: round2(receiptTotal - expense - stock) }
}

export type LineProblem = '' | 'category' | 'amount' | 'item' | 'qty' | 'price' | 'expiry'

/** `item` (optional) is the line's stock item: one tracked for expiry needs a
 *  use-by date, or the server refuses the stock approval. */
export function lineProblem(l: ReviewLine, item?: { tracks_expiry?: boolean }): LineProblem {
  if (l.kind === 'expense') {
    if (!l.category) return 'category'
    if (!(num(l.amount) > 0)) return 'amount'
    return ''
  }
  if (!l.stockItemId) return 'item'
  if (!(num(l.qty) > 0)) return 'qty'
  if (!(num(l.unitPrice) >= 0) || l.unitPrice.trim() === '') return 'price'
  if (item?.tracks_expiry && !(l.expiryDate ?? '').trim()) return 'expiry'
  return ''
}

export const LINE_PROBLEM_TEXT: Record<Exclude<LineProblem, ''>, string> = {
  category: 'Pick a category',
  amount: 'Enter the amount',
  item: 'Pick the stock item',
  qty: 'Enter the quantity',
  price: 'Enter the price',
  expiry: 'Enter the use-by date',
}

/** Which parts this scan still waits for. */
export function pendingParts(scan: Pick<ReceiptScan, 'expense_approved_at' | 'stock_approved_at'>, f: ReviewForm) {
  const hasExpense = f.lines.some(l => l.kind === 'expense')
  const hasStock = f.lines.some(l => l.kind === 'stock')
  return {
    expense: hasExpense && !scan.expense_approved_at,
    stock: hasStock && !scan.stock_approved_at,
  }
}

export function expensePayload(f: ReviewForm, stockPending: boolean): ApproveReceiptExpensesInput {
  return {
    expense_date: f.date,
    stock_pending: stockPending,
    vendor_id: f.supplierId,
    payee: f.supplierId ? '' : f.payee.trim(),
    payment_method: f.paymentMethod,
    reference_no: f.referenceNo.trim(),
    notes: f.notes.trim(),
    lines: f.lines
      .filter(l => l.kind === 'expense')
      .map(l => ({ category: l.category, amount: round2(num(l.amount)), description: l.description.trim(), raw_text: learnText(l) })),
  }
}

/**
 * Stock lines go to the server in the item's stock unit: "2 Bags at Rs 5,000"
 * of a 25 kg bag is 50 kg at Rs 200 per kg. The money is unchanged.
 */
export function stockPayload(f: ReviewForm, options: ReceiptScanOptions | undefined, expensePending: boolean): ApproveReceiptStockInput {
  const items = new Map((options?.stock_items ?? []).map(i => [i.id, i]))
  const supplierName = f.supplierId ? undefined : f.payee.trim() || undefined
  return {
    supplier_id: f.supplierId || undefined,
    supplier_name: supplierName,
    paid_by: options?.kitchen_flow ? f.paidBy : undefined,
    buyer_name: options?.kitchen_flow && f.paidBy === 'buyer' ? f.buyerName.trim() : undefined,
    reference_no: f.referenceNo.trim() || undefined,
    notes: f.notes.trim() || undefined,
    expense_pending: expensePending,
    lines: f.lines
      .filter(l => l.kind === 'stock')
      .map(l => {
        const item = items.get(l.stockItemId)
        const factor = l.unitMode === 'purchase' && item?.purchase_unit_factor ? item.purchase_unit_factor : 1
        const qty = num(l.qty) * factor
        return {
          raw_text: learnText(l),
          stock_item_id: l.stockItemId,
          qty: Math.round(qty * 100000) / 100000,
          unit_cost: Math.round((num(l.unitPrice) / factor) * 10000) / 10000,
          ...(l.expiryDate?.trim() ? { expiry_date: l.expiryDate.trim() } : {}),
          ...(l.lotNo?.trim() ? { lot_no: l.lotNo.trim() } : {}),
        }
      }),
  }
}

/** The text learning keys on: the line as printed, or what the approver
 *  typed for a line they added by hand. */
function learnText(l: ReviewLine): string {
  return (l.rawText || l.description).trim()
}

export const RECEIPT_STATUS_LABEL: Record<ReceiptScanStatus, string> = {
  queued: 'Waiting to be read',
  processing: 'Reading…',
  ready: 'Ready to review',
  failed: "Couldn't read",
  partly_approved: 'Partly approved',
  approved: 'Approved',
  discarded: 'Discarded',
}

/** Still being read: keep polling. */
export function isReading(s: ReceiptScanStatus | undefined): boolean {
  return s === 'queued' || s === 'processing'
}

export const DATE_ISSUE_TEXT: Record<'missing' | 'future' | 'old', string> = {
  missing: "The date wasn't readable — check it.",
  future: 'The date read as a future date — check it.',
  old: 'The date is over three months ago — check it.',
}

// ── Receipts inbox (F-FE-N192) ─────────────────────────────────────────────

export type InboxTab = 'review' | 'reading' | 'failed' | 'done'

export const INBOX_TABS: { key: InboxTab; label: string; statuses: ReceiptScanStatus[] }[] = [
  { key: 'review', label: 'To review', statuses: ['ready', 'partly_approved'] },
  { key: 'reading', label: 'Being read', statuses: ['queued', 'processing'] },
  { key: 'failed', label: "Couldn't read", statuses: ['failed'] },
  { key: 'done', label: 'Done', statuses: ['approved', 'discarded'] },
]

export function inboxTabOf(s: ReceiptScanStatus): InboxTab {
  return INBOX_TABS.find(t => t.statuses.includes(s))?.key ?? 'review'
}

export function inboxCount(counts: Partial<Record<ReceiptScanStatus, number>>, tab: InboxTab): number {
  return (INBOX_TABS.find(t => t.key === tab)?.statuses ?? []).reduce((n, s) => n + (counts[s] ?? 0), 0)
}

/** The order review mode steps through: oldest scan first, as a pile is read. */
export function reviewQueue(scans: Pick<ReceiptScan, 'id' | 'status' | 'created_at'>[]): string[] {
  return scans
    .filter(s => s.status === 'ready' || s.status === 'partly_approved')
    .slice()
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map(s => s.id)
}

/** After finishing one receipt, the next still waiting in the queue (or null). */
export function nextInQueue(queue: string[], doneId: string, stillOpen: Set<string>): string | null {
  const i = queue.indexOf(doneId)
  const after = [...queue.slice(i + 1), ...queue.slice(0, Math.max(i, 0))]
  return after.find(id => id !== doneId && stillOpen.has(id)) ?? null
}

/** One line that tells receipts apart in a list. */
export function scanHeadline(s: Pick<ReceiptScan, 'draft' | 'status'>): { vendor: string; date: string; total: number } {
  const e = s.draft?.extraction
  return { vendor: e?.vendor_name?.trim() || (s.status === 'failed' ? 'Unreadable receipt' : 'Receipt'), date: e?.receipt_date || '', total: e?.total ?? 0 }
}
